import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { CardProps } from "./types";
import { useContext, useEffect, useState } from "react";
import { CardModal } from "./CardModal";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";
import { mapToTalkUser } from "../../utils/TalkjsUtils";
import Mixpanel from "../../utils/mixpanel";
import { StyledCard, StyledCardActionArea } from "./styles";
import { StyledCardMedia, StyledCardText, StyledProfilePic } from "./styles";
import { NavLink, useNavigate } from "react-router-dom";
import { styles } from "./styles";
import AvatarGroup from "@mui/material/AvatarGroup";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { APP_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";

const CustomizedTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: APP_COLOR,
    color: "white",
    boxShadow: theme.shadows[1],
    fontSize: 12,
    borderRadius: 10,
    fontFamily: FONT_FAMILY,
    padding: 8,
  },
}));

const CardNFT: React.FC<CardProps> = ({
  nft,
  stopAllSongs,
  handlePlayPause,
  isTrack,
}) => {
  const { id, title, artist, image, song, contributors, isPlaying } = nft;
  const [audio] = useState(new Audio());
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { userId } = useContext<AuthContextInterface>(AuthContext);

  useEffect(() => {
    if (audio) {
      if (audio.paused) audio.src = song;
      audio.preload = "none";
      isPlaying ? audio.play() : audio.pause();
    }
  }, [audio, isPlaying, song]);

  useEffect(() => {
    audio && audio.addEventListener("ended", () => stopAllSongs());
    return () => {
      audio && audio.removeEventListener("ended", () => stopAllSongs());
    };
  }, [audio, id, stopAllSongs]);

  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const [createGroupChatClicked, setCreateGroupChatClicked] = useState(false);

  const handleCreateGroupChat = () => {
    try {
      if (talkJsContext && contributors) {
        const participants = contributors
          .filter((c) => c.getUsername())
          .map(mapToTalkUser);
        if (window.innerWidth > 450) {
          talkJsContext.handleChatClick();
          talkJsContext.setParticipants(participants);
          setCreateGroupChatClicked(true);
        } else {
          talkJsContext.setParticipants(participants);
          talkJsContext.setAutoCreateChat(true);
          navigate(`/messages`);
        }

        Mixpanel.track("create_chat_grop_card");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (createGroupChatClicked && talkJsContext) {
      talkJsContext.createConversation();
      setOpen(false);
    }
    return () => {
      audio && audio.pause();
    };
  }, [talkJsContext, createGroupChatClicked, audio]);

  const handleOpen = () => {
    if (isTrack && contributors.some((c) => c.getUserId() === userId)) {
      stopAllSongs();
      navigate(`/track/${id}`);
    } else {
      stopAllSongs();
      Mixpanel.track("open_track_card");
      setOpen(true);
    }
  };
  const handleClose = () => setOpen(false);

  return (
    <>
      {/* Modal only for NFTs and not tracks */}
      {open && (
        <CardModal
          open={open}
          handleClose={handleClose}
          nft={nft}
          handleCreateGroupChat={handleCreateGroupChat}
          isTrack={isTrack}
        />
      )}

      <StyledCard>
        <StyledCardActionArea onClick={handleOpen}>
          <StyledCardMedia
            // component="img"
            image={
              image?.includes("http") ? image : `/assets/images/logo512.png`
            }
            // alt="Album cover"
          />
        </StyledCardActionArea>
        <Box sx={styles.infoContainer}>
          <Box sx={styles.iconContainer}>
            <IconButton
              sx={{ color: TEXT_COLOR }}
              aria-label="play/pause"
              onClick={() => handlePlayPause(id!)}
            >
              {isPlaying ? (
                <PauseIcon sx={styles.icon} />
              ) : (
                <PlayArrowIcon sx={styles.icon} />
              )}
            </IconButton>
          </Box>
          <Box sx={{ flex: 3 }}>
            <StyledCardText
              fontFamily={FONT_FAMILY}
              variant="h6"
              sx={{ color: TEXT_COLOR }}
            >
              {title}
            </StyledCardText>
            <StyledCardText
              sx={{ color: TEXT_COLOR }}
              variant="subtitle2"
              color="text.secondary"
              fontFamily={FONT_FAMILY}
              fontSize={13}
              fontWeight="none"
            >
              {`By ${
                artist === ""
                  ? contributors.map((c) => c.getUsername()).join(", ")
                  : artist
              }`}
            </StyledCardText>
          </Box>
          {nft.contributors.length > 1 ? (
            <CustomizedTooltip
              style={{ cursor: "pointer" }}
              title={
                <Box sx={{ display: "flex" }}>
                  {nft.contributors.map((el) => {
                    const userProfileRoute = `/user-profile/${el.getUsername()}`;
                    return (
                      <CustomizedTooltip
                        title={el.getUsername()}
                        key={el.getUserId()}
                        placement="top"
                      >
                        <NavLink
                          to={userProfileRoute}
                          state={{ userId: el.getUserId() }}
                          style={styles.navLinkAvatar}
                          key={el.getUserId()}
                        >
                          <StyledProfilePic
                            key={el.getUsername()}
                            variant={"rounded"}
                            alt="profile picture"
                            src={el.getProfilePicture()}
                          />
                        </NavLink>
                      </CustomizedTooltip>
                    );
                  })}
                </Box>
              }
              placement="top-end"
              enterTouchDelay={0}
            >
              <Box sx={styles.contributorsContainer}>
                <AvatarGroup max={2} sx={styles.avatarGroup}>
                  {nft.contributors.map((el) => {
                    const userProfileRoute = `/user-profile/${el.getUsername()}`;
                    return (
                      <NavLink
                        to={userProfileRoute}
                        state={{ userId: el.getUserId() }}
                        style={styles.navLink}
                        key={el.getUserId()}
                      >
                        <StyledProfilePic
                          key={el.getUsername()}
                          style={styles.styledProfilePic}
                          variant={"rounded"}
                          alt="profile picture"
                          src={el.getProfilePicture()}
                        />
                      </NavLink>
                    );
                  })}
                </AvatarGroup>
              </Box>
            </CustomizedTooltip>
          ) : (
            <Box sx={styles.contributorsContainer}>
              <AvatarGroup max={2}>
                {nft.contributors.map((el) => {
                  const userProfileRoute = `/user-profile/${el.getUsername()}`;
                  return (
                    <CustomizedTooltip
                      title={el.getUsername()}
                      key={el.getUserId()}
                      placement="top"
                    >
                      <NavLink
                        to={userProfileRoute}
                        state={{ userId: el.getUserId() }}
                        style={styles.navLink}
                        key={el.getUserId()}
                      >
                        <StyledProfilePic
                          key={el.getUsername()}
                          style={styles.styledProfilePic}
                          variant={"rounded"}
                          alt="profile picture"
                          src={el.getProfilePicture()}
                        />
                      </NavLink>
                    </CustomizedTooltip>
                  );
                })}
              </AvatarGroup>
            </Box>
          )}
        </Box>
      </StyledCard>
    </>
  );
};

export default CardNFT;
